import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Trans, useTranslation } from 'next-i18next';
import { exists } from 'i18next';

import StyledContainer from '@design-system/styled/Container';
import useRegionConfig from '@source/hooks/useRegionConfig';

import useMobileView from '@design-system/hooks/useMobileView';
import CCPOFeaturedIn from './CCPOFeaturedIn';
import { HIGHLIGHTS_CONTENT } from '../../constants';

const StyledHighLightContainer = styled.section`
  background-color: #ede9e8;
  padding-top: 62px;
  padding-bottom: 73px;
  padding-left: 43px;
  padding-right: 43px;
  @media (min-width: 768px) {
    padding-top: 79px;
    padding-bottom: 100px;
  }
`;

const CustomStyledContainer = styled(StyledContainer)`
  @media only screen and (min-width: 1280px) {
    width: 100%;
    max-width: 1700px;
  }
`;

const StyledTitle = styled.h2`
  font-size: 18px;
  line-height: 25px;
  font-weight: bold;
  text-align: center;
  position: relative;
  margin-bottom: 34px;
  color: #3b3b3b;
  ${(props) => props.theme.typo.familyGoogle.bold};

  @media (min-width: 768px) {
    font-size: 28px;
    line-height: 39px;
    margin-bottom: 48px;
  }
`;

const StyledWrapper = styled.ul`
  position: relative;
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  align-items: center;
  justify-content: center;
  padding-bottom: 5px;
  border-bottom: 1px solid rgba(161, 161, 161, 0.4);
  list-style: none;
  margin: 0;
  padding: 0;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, minmax(min-content, max-content));
    border-bottom: unset;
    padding-bottom: 50px;
  }

  @media (min-width: 1280px) {
    padding-bottom: 62px;
  }
`;

const StyledItem = styled.li<{ country?: string }>`
  padding: 30px 0;
  width: 100%;
  text-align: center;

  &:not(:last-child) {
    border-bottom: 1px solid ${(props) => props.theme.color.primaryBase};
    @media (min-width: 768px) {
      border-bottom: unset;
      border-right: 1px solid ${(props) => props.theme.color.primaryBase};
    }
  }

  .text {
    ${(props) => props.theme.typo.familyGoogle.bold};
    font-size: 21px;
    line-height: 25px;

    ${(props) =>
      props.country !== 'my' &&
      css`
        display: block;
      `}
  }

  .text-title {
    color: ${(props) => props.theme.color.onBackgroundMediumEmphasis};
  }
  .text-highlight {
    color: ${(props) => props.theme.color.primaryBase};
  }
  @media (min-width: 768px) {
    padding: 0 10px;
    display: flex;
    align-items: center;
    height: 100%;
  }
  @media (min-width: 992px) {
    padding: 0 20px;

    &:not(:last-child) {
      border-right: 1px solid ${(props) => props.theme.color.primaryBase};
    }
    .text {
      display: inline;
    }
  }

  @media (min-width: 1280px) {
    padding: 0 30px;
  }
`;

const CCPOPageHighlights = () => {
  const { i18n, t } = useTranslation('ccpo');
  const {
    countryConfig: { country },
  } = useRegionConfig();
  const isMobile = useMobileView(576);

  // Get type of `start to talking with us` CTA
  const contentByCountry = useMemo(
    () =>
      HIGHLIGHTS_CONTENT?.filter((item: { key: string; title: string; highlight: string }) => {
        const isSG = country === 'sg';
        const isFirstSing = item?.key === 'first-sing';
        const isHK = country === 'hk';
        const isFirstAsiaHK = item?.key === 'first-asia-hk';
        if (isFirstSing && !isSG) {
          return false;
        }
        if (isFirstAsiaHK && !isHK) {
          return false;
        }
        if (item?.key === 'first-asia' && isHK) {
          return false;
        }
        return true;
      }).map((item) => {
        if (country !== 'my') {
          return { ...item, endText: '' };
        }

        return item;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [country],
  );

  return (
    <StyledHighLightContainer role="region" aria-label="Carro Certified Program Highlights">
      <CustomStyledContainer>
        <StyledTitle>
          <Trans
            ns="ccpo"
            defaults={t(`ccpo.highlights.title.${country ?? 'sg'}`)}
            components={{ brD: isMobile ? <span /> : <br />, brM: isMobile ? <br /> : <span /> }}
          />
        </StyledTitle>
        <StyledWrapper role="list" aria-label="Program Highlights">
          {contentByCountry.map(
            (item) =>
              i18n.exists(item.title) && (
                <StyledItem key={item.title} country={country} role="listitem">
                  <div>
                    <span className="text text-title">
                      <Trans
                        ns="ccpo"
                        defaults={item.title}
                        components={{
                          br: <br />,
                          specificFont: <span style={{ fontFamily: 'Poppins' }} />,
                          brD: isMobile ? <span /> : <br />,
                          brM: isMobile ? <br /> : <span />,
                        }}
                      />
                    </span>
                    <span className="text text-highlight">
                      <Trans
                        ns="ccpo"
                        defaults={item.highlight}
                        components={{
                          br: <br />,
                          nocolor: <span style={{ color: '#A1A1A1' }} />,
                          specificFont: <span style={{ fontFamily: 'Poppins' }} />,
                          brD: isMobile ? <span /> : <br />,
                          brM: isMobile ? <br /> : <span />,
                        }}
                      />
                    </span>
                    {item.endText && exists(item.endText) && (
                      <span className="text text-title">
                        <Trans
                          ns="ccpo"
                          defaults={item.endText}
                          components={{
                            br: <br />,
                            specificFont: <span style={{ fontFamily: 'Poppins' }} />,
                            brD: isMobile ? <span /> : <br />,
                            brM: isMobile ? <br /> : <span />,
                          }}
                        />
                      </span>
                    )}
                  </div>
                </StyledItem>
              ),
          )}
        </StyledWrapper>
        <CCPOFeaturedIn />
      </CustomStyledContainer>
    </StyledHighLightContainer>
  );
};

export default CCPOPageHighlights;
